'use client'
import { Popup } from '@/components/Popup'
import { Icon } from '@/icons'
import { BackButton } from '@/shared/Auth/components/BackButton'
import { Tabs } from '@/shared/Auth/components/Tabs'
import { ConfirmResetPassword } from '@/shared/Auth/modules/ConfirmResetPassword'
import { Login } from '@/shared/Auth/modules/Login'
import { PasswordReset } from '@/shared/Auth/modules/PasswordReset'
import { PasswordResetRequest } from '@/shared/Auth/modules/PasswordResetRequest'
import { Registration } from '@/shared/Auth/modules/Registration'
import { authStore, SCREEN_LIST, TabsOptions } from '@/store/Auth'
import { popupAuthStore } from '@/store/PopupAuth.store'
import { observer } from 'mobx-react-lite'
import { FC, useCallback } from 'react'
import { EmailVerified } from './components/EmailVerified'
import styles from './PopupAuth.module.scss'

interface PopupAuthProps {
  onClose: () => void
}
export const PopupAuth: FC<PopupAuthProps> = observer(({ onClose }) => {
  const close = useCallback(() => {
    authStore.resetData()
    onClose()
  }, [onClose])

  const login = useCallback(() => {
    authStore.login().then((response) => {
      if (response.status) {
        popupAuthStore.hide()
        setTimeout(() => {
          authStore.resetData()
        }, 1000)
      }
    })
  }, [])

  const registration = useCallback(() => {
    authStore.registration()
  }, [])

  const saveNewPassword = useCallback(async () => {
    try {
      const { status } = await authStore.saveNewPassword()
      if (status) {
        authStore.setScreen(SCREEN_LIST.LOGIN)
      }
    } catch {}
  }, [])

  return (
    <Popup onClose={close} className={styles.popup}>
      <Icon name="logo" className={styles.logo} />

      {authStore.screen === SCREEN_LIST.PASSWORD_RESET_REQUEST ||
      authStore.screen === SCREEN_LIST.CONFIRM_RESET_PASSWORD ||
      authStore.screen === SCREEN_LIST.PASSWORD_RESET ? (
        <BackButton onClick={authStore.goToLogin} />
      ) : null}

      {authStore.screen === SCREEN_LIST.LOGIN ||
      authStore.screen === SCREEN_LIST.REGISTRATION ? (
        <Tabs
          values={TabsOptions}
          onChange={authStore.setScreen}
          value={authStore.screen}
        />
      ) : null}

      {authStore.screen === SCREEN_LIST.LOGIN ? (
        <Login
          isLoading={authStore.loading}
          email={authStore.form.email}
          password={authStore.form.password}
          changeEmail={authStore.setEmail}
          changePassword={authStore.setPassword}
          errors={authStore.validator?.errors}
          onLogin={login}
          goToResetPassword={authStore.goToResetPassword}
        />
      ) : null}

      {authStore.screen === SCREEN_LIST.REGISTRATION ? (
        <Registration
          isLoading={authStore.loading}
          name={authStore.form.name}
          phone={authStore.form.phone}
          password={authStore.form.password}
          email={authStore.form.email}
          acceptPolicy={authStore.form.acceptPolicy}
          changeName={authStore.setName}
          changePhone={authStore.setPhone}
          changePassword={authStore.setPassword}
          changeEmail={authStore.setEmail}
          changeAcceptPolicy={authStore.setAcceptPolicy}
          errors={authStore.validator?.errors}
          onRegistration={registration}
        />
      ) : null}

      {authStore.screen === SCREEN_LIST.PASSWORD_RESET_REQUEST ? (
        <PasswordResetRequest
          isLoading={authStore.loading}
          email={authStore.form.email}
          changeEmail={authStore.setEmail}
          errors={authStore.validator?.errors}
          resetPassword={authStore.passwordResetRequest}
        />
      ) : null}

      {authStore.screen === SCREEN_LIST.CONFIRM_RESET_PASSWORD ? (
        <ConfirmResetPassword
          email={authStore.form.email}
          code={authStore.confirmResetPasswordCode}
          setCode={authStore.setConfirmResetPasswordCode}
          error={authStore.confirmResetPasswordError}
          onRequestRetry={authStore.passwordResetRequest}
        />
      ) : null}

      {authStore.screen === SCREEN_LIST.PASSWORD_RESET ? (
        <PasswordReset
          isLoading={authStore.loading}
          newPassword={authStore.newPassword}
          setNewPassword={authStore.setNewPassword}
          retryNewPassword={authStore.retryNewPassword}
          setRetryNewPassword={authStore.setRetryNewPassword}
          saveNewPassword={saveNewPassword}
          errors={authStore.validator?.errors}
        />
      ) : null}

      {authStore.screen === SCREEN_LIST.REGISTRATION_SUCCESS ? (
        <EmailVerified email={authStore.form.email} isSuccessRegistration />
      ) : null}

      {authStore.screen === SCREEN_LIST.EMAIL_NOT_VERIFIED ? (
        <EmailVerified email={authStore.form.email} />
      ) : null}
    </Popup>
  )
})
